import fallbackPublicVehicles from '../data/publicVehiclesFallback.json';

const NOVPRAYAN_PUBLIC_API_BASE_URL =
  process.env.REACT_APP_NOVPRAYAN_PUBLIC_API_URL || 'https://apps-api.novprayan.com/api/public';
const NOVPRAYAN_TENANT_ID = process.env.REACT_APP_NOVPRAYAN_TENANT_ID;
const NOVPRAYAN_COMPANY_CODE = process.env.REACT_APP_NOVPRAYAN_COMPANY_CODE || 'PL';
const VEHICLE_API_CACHE_WINDOW_MS = 30 * 60 * 1000;

const getNovprayanHeaders = () => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (NOVPRAYAN_TENANT_ID) {
    headers['X-Tenant-ID'] = NOVPRAYAN_TENANT_ID;
  } else if (NOVPRAYAN_COMPANY_CODE) {
    headers['X-Company-Code'] = NOVPRAYAN_COMPANY_CODE;
  }

  return headers;
};

const rupees = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';

  return `₹${amount.toLocaleString('en-IN', {
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  })}`;
};

const normalizeText = (value = '') => String(value || '').trim().toLowerCase();
const hasValue = (value) => value !== undefined && value !== null && value !== '';
const toFiniteNumber = (value) => {
  if (!hasValue(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const toPositiveInteger = (value) => {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
};
const getVehicleBackendId = (vehicle = {}) => {
  const candidates = [
    vehicle.backendVehicleId,
    vehicle.vehicleId,
    vehicle.id,
    vehicle.apiVehicle?.backendVehicleId,
    vehicle.apiVehicle?.vehicleId,
    vehicle.apiVehicle?.id,
    vehicle.vehicle?.backendVehicleId,
    vehicle.vehicle?.vehicleId,
    vehicle.vehicle?.id,
    vehicle.rawVehicle?.backendVehicleId,
    vehicle.rawVehicle?.vehicleId,
    vehicle.rawVehicle?.id,
    vehicle.sourceVehicle?.backendVehicleId,
    vehicle.sourceVehicle?.vehicleId,
    vehicle.sourceVehicle?.id,
    String(vehicle.catalogKey || '').match(/^id-(\d+)$/)?.[1],
  ];

  for (const candidate of candidates) {
    const id = toPositiveInteger(candidate);

    if (id !== null) return id;
  }

  return null;
};
const getBaseRateValue = (rateCard = {}) => (
  hasValue(rateCard.baseRate) ? rateCard.baseRate : rateCard.rate
);
const hasBaseRate = (rateCard = {}) => {
  const baseRate = toFiniteNumber(getBaseRateValue(rateCard));
  return baseRate !== null && baseRate > 0;
};

const isAirportRateCard = (rateCard = {}) => normalizeText(rateCard.tripType).includes('airport');
const isOutstationRateCard = (rateCard = {}) => normalizeText(rateCard.tripType).includes('outstation');
const isLocalRateCard = (rateCard = {}) => normalizeText(rateCard.tripType).includes('local');

const normalizeVehicleOrderText = (value = '') =>
  normalizeText(value)
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9+]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getVehicleOrderText = (vehicle = {}) => {
  const source = vehicle.apiVehicle || vehicle.rawVehicle || vehicle.sourceVehicle || vehicle.vehicle || vehicle;

  return normalizeVehicleOrderText(
    [
      source.vehicleType,
      source.vehicleCategory,
      vehicle.vehicleType,
      vehicle.vehicleCategory,
      vehicle.name,
      vehicle.type,
      vehicle.category,
      vehicle.models,
      source.capacity,
      vehicle.capacity,
      vehicle.seats,
      vehicle.seaters,
    ]
      .filter(Boolean)
      .join(' ')
  );
};

const getVehicleTypeOrderText = (vehicle = {}) => {
  const source = vehicle.apiVehicle || vehicle.rawVehicle || vehicle.sourceVehicle || vehicle.vehicle || vehicle;

  return normalizeVehicleOrderText(source.vehicleType || vehicle.vehicleType || vehicle.name || '');
};

const hasAllOrderTokens = (text, tokens) =>
  tokens.every((token) => text.includes(normalizeVehicleOrderText(token)));

const hasAnyOrderToken = (text, tokens) =>
  tokens.some((token) => text.includes(normalizeVehicleOrderText(token)));

const hasAllVehicleTypeTokens = (vehicle, tokens) =>
  hasAllOrderTokens(getVehicleTypeOrderText(vehicle), tokens);
const isNormalVehicleVariant = (vehicle) =>
  !hasAnyOrderToken(getVehicleTypeOrderText(vehicle), ['luxury', 'maharaja']);
const isPlainUrbania = (vehicle) => {
  const typeText = getVehicleTypeOrderText(vehicle);

  return (
    hasAllOrderTokens(typeText, ['urbania']) &&
    !hasAnyOrderToken(typeText, ['9+1', '12+1', '16+1', 'luxury', 'maharaja'])
  );
};
const isPlainTempoTraveller = (vehicle) => {
  const typeText = getVehicleTypeOrderText(vehicle);

  return (
    hasAllOrderTokens(typeText, ['tempo', 'traveller']) &&
    !hasAnyOrderToken(typeText, ['7/9/12', '12+1', '13+1', 'luxury', 'maharaja'])
  );
};
const isTempoTravellerVariant = (vehicle) => {
  const typeText = getVehicleTypeOrderText(vehicle);

  return hasAllOrderTokens(typeText, ['tempo', 'traveller']) && !isPlainTempoTraveller(vehicle);
};

const VEHICLE_DISPLAY_ORDER = {
  airport: [
    (text) => hasAllOrderTokens(text, ['sedan']),
    (text) => hasAllOrderTokens(text, ['etios']),
    (text) => hasAllOrderTokens(text, ['ertiga']),
    (text) => hasAllOrderTokens(text, ['carens']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta', '7+1']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta', '6+1']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta']),
    (text) => hasAllOrderTokens(text, ['innova', 'hybrid', '7+1']),
    (text) => hasAllOrderTokens(text, ['hycross', 'hybrid']),
    (text) => hasAllOrderTokens(text, ['fortuner']),
    (text) => hasAllOrderTokens(text, ['carnival']),
    (text, vehicle) => isPlainTempoTraveller(vehicle),
    (text) => hasAllOrderTokens(text, ['tempo', '12+1']),
    (text) => hasAllOrderTokens(text, ['tempo', '13+1']),
    (text, vehicle) => isTempoTravellerVariant(vehicle),
    (text, vehicle) => isPlainUrbania(vehicle) || (hasAllVehicleTypeTokens(vehicle, ['urbania', '9+1']) && isNormalVehicleVariant(vehicle)),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '9+1']),
    (text) => hasAllOrderTokens(text, ['urbania', '12+1']) && hasAnyOrderToken(text, ['luxury', 'maharaja']),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '16+1']),
    (text) => hasAllOrderTokens(text, ['bus']),
  ],
  local: [
    (text) => hasAllOrderTokens(text, ['sedan']),
    (text) => hasAllOrderTokens(text, ['etios']),
    (text) => hasAllOrderTokens(text, ['ertiga']),
    (text) => hasAllOrderTokens(text, ['carens']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta']),
    (text) => hasAllOrderTokens(text, ['innova', 'hybrid', '7+1']),
    (text) => hasAllOrderTokens(text, ['hycross', 'hybrid']),
    (text) => hasAllOrderTokens(text, ['fortuner']),
    (text) => hasAllOrderTokens(text, ['carnival']),
    (text) => hasAllOrderTokens(text, ['tempo', '12+1']),
    (text) => hasAllOrderTokens(text, ['tempo', '13+1']),
    (text, vehicle) => isPlainTempoTraveller(vehicle),
    (text, vehicle) => isTempoTravellerVariant(vehicle),
    (text, vehicle) => isPlainUrbania(vehicle) || (hasAllVehicleTypeTokens(vehicle, ['urbania', '9+1']) && isNormalVehicleVariant(vehicle)),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '9+1']),
    (text, vehicle) => hasAllVehicleTypeTokens(vehicle, ['urbania', '12+1']) && isNormalVehicleVariant(vehicle),
    (text) => hasAllOrderTokens(text, ['urbania', '12+1']) && hasAnyOrderToken(text, ['luxury', 'maharaja']),
    (text) => hasAllOrderTokens(text, ['urbania', '16+1']),
    (text) => hasAllOrderTokens(text, ['bus']),
  ],
  outstation: [
    (text) => hasAllOrderTokens(text, ['sedan']),
    (text) => hasAllOrderTokens(text, ['etios']),
    (text) => hasAllOrderTokens(text, ['ertiga']),
    (text) => hasAllOrderTokens(text, ['carens']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta', '7+1']),
    (text) => hasAllOrderTokens(text, ['innova', 'crysta']),
    (text) => hasAllOrderTokens(text, ['innova', 'hybrid', '7+1']),
    (text) => hasAllOrderTokens(text, ['fortuner']),
    (text) => hasAllOrderTokens(text, ['carnival']),
    (text, vehicle) => isPlainTempoTraveller(vehicle),
    (text, vehicle) => isTempoTravellerVariant(vehicle),
    (text, vehicle) => isPlainUrbania(vehicle),
    (text, vehicle) => hasAllVehicleTypeTokens(vehicle, ['urbania', '9+1']) && isNormalVehicleVariant(vehicle),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '9+1']),
    (text, vehicle) => hasAllVehicleTypeTokens(vehicle, ['urbania', '12+1']) && isNormalVehicleVariant(vehicle),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '12+1']),
    (text, vehicle) => hasAllVehicleTypeTokens(vehicle, ['urbania', '16+1']) && isNormalVehicleVariant(vehicle),
    (text) => hasAllOrderTokens(text, ['urbania', 'luxury', '16+1']),
    (text) => hasAllOrderTokens(text, ['bus']),
  ],
};

const getRateCardForTrip = (vehicle, trip) => {
  const rateCards = Array.isArray(vehicle.rateCards) ? vehicle.rateCards : [];
  let tripRateCards = rateCards;

  if (trip === 'airport') {
    tripRateCards = rateCards.filter(isAirportRateCard);
  } else if (trip === 'outstation') {
    tripRateCards = rateCards.filter(isOutstationRateCard);
  } else if (trip === 'local') {
    tripRateCards = rateCards.filter(isLocalRateCard);
  }

  return tripRateCards.find(hasBaseRate) || tripRateCards[0];
};

const getVehicleCatalogKey = (vehicle = {}) => {
  const vehicleBackendId = getVehicleBackendId(vehicle);

  if (vehicleBackendId) return `id-${vehicleBackendId}`;
  if (vehicle.registrationNumber) return `reg-${normalizeText(vehicle.registrationNumber)}`;

  const type = normalizeText(vehicle.vehicleType || 'vehicle').replace(/\s+/g, ' ');
  return type || `vehicle-${vehicleBackendId || vehicle.registrationNumber || ''}`;
};

const mergeRateCards = (primaryCards = [], secondaryCards = []) => {
  const merged = [...primaryCards];

  secondaryCards.forEach((card) => {
    const index = merged.findIndex((item) => (
      normalizeText(item.tripType) === normalizeText(card.tripType) &&
      normalizeText(item.packageName) === normalizeText(card.packageName) &&
      String(item.maxHours || '') === String(card.maxHours || '') &&
      String(item.maxDays || '') === String(card.maxDays || '') &&
      String(item.maxKilometers || '') === String(card.maxKilometers || '')
    ));

    if (index === -1) {
      merged.push(card);
      return;
    }

    if (!hasBaseRate(merged[index]) && hasBaseRate(card)) {
      merged[index] = card;
    }
  });

  return merged;
};

const mergeCatalogVehicles = (vehicles = []) => {
  const grouped = new Map();

  vehicles.forEach((vehicle) => {
    if (!vehicle?.status) return;

    const key = getVehicleCatalogKey(vehicle);
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        ...vehicle,
        rateCards: Array.isArray(vehicle.rateCards) ? [...vehicle.rateCards] : [],
      });
      return;
    }

    const existingHasPositiveRate = (existing.rateCards || []).some(hasBaseRate);
    const vehicleHasPositiveRate = (vehicle.rateCards || []).some(hasBaseRate);
    const base = !existingHasPositiveRate && vehicleHasPositiveRate ? vehicle : existing;
    const fallback = base === vehicle ? existing : vehicle;

    grouped.set(key, {
      ...fallback,
      ...base,
      rateCards: mergeRateCards(existing.rateCards || [], vehicle.rateCards || []),
    });
  });

  return Array.from(grouped.values());
};

const getVehicleDisplayName = (vehicle = {}) => {
  const type = String(vehicle.vehicleType || 'Vehicle').replace(/_/g, ' ');
  return type;
};

const getImageKey = (vehicle = {}) => {
  const type = String(vehicle.vehicleType || '').toLowerCase();
  const name = `${vehicle.vehicleType || ''} ${vehicle.vehicleCategory || ''}`.toLowerCase();

  if (type.includes('sedan') || type.includes('hatch')) return 'sedan';
  if (type.includes('etios')) return 'premiumSedan';
  if (name.includes('bus')) return 'buss';
  if (name.includes('tempo') || name.includes('traveller')) return 'tempo';
  if (name.includes('urbania')) return 'Urbania';
  if (name.includes('carens')) return 'kiaCarens';
  if (name.includes('carnival')) return 'kiaCarnival';
  if (name.includes('fortuner')) return 'fortuner';
  if (name.includes('suv') || name.includes('ertiga') || name.includes('rumion')) return 'ertiga';
  if (name.includes('hycross') || name.includes('hybrid')) return 'hycross';
  if (name.includes('innova') || name.includes('luxury')) return 'crysta';

  return 'sedan';
};

const getConfiguredSeats = (vehicle = {}) => {
  const text = normalizeVehicleOrderText(
    [
      vehicle.vehicleType,
      vehicle.vehicleCategory,
      vehicle.name,
      vehicle.models,
    ]
      .filter(Boolean)
      .join(' ')
  );

  if (!text) return null;

  if (text.includes('sedan') || text.includes('etios')) return 4;
  if (text.includes('ertiga')) return 6;
  if (text.includes('carens')) return 6;
  if (text.includes('fortuner')) return 6;
  if (text.includes('carnival')) return 6;
  if (text.includes('innova crysta')) return 7;
  if (text.includes('hycross') || text.includes('hybrid')) return 7;
  if (text.includes('tempo') || text.includes('traveller')) {
    if (text.includes('13+1') || text.includes('13 seater')) return 13;
    return 12;
  }
  if (text.includes('urbania')) {
    if (text.includes('16+1') || text.includes('16 seater')) return 16;
    if (text.includes('12+1') || text.includes('12 seater') || text.includes('maharaja')) return 12;
    if (text.includes('9+1') || text.includes('9 seater')) return 9;
    return 9;
  }
  if (text.includes('bus')) return 50;

  return null;
};

const getSeats = (vehicle = {}) => {
  const configuredSeats = getConfiguredSeats(vehicle);
  if (configuredSeats !== null) return configuredSeats;

  if (vehicle.capacity) return vehicle.capacity;

  const type = String(vehicle.vehicleType || '').toLowerCase();
  if (type.includes('bus')) return 50;
  if (type.includes('tempo') || type.includes('traveller')) return 12;
  if (type.includes('urbania')) return 13;
  if (type.includes('suv')) return 7;

  return 4;
};

const getBags = (seats) => {
  const value = Number(seats);
  if (value >= 21) return 20;
  if (value >= 12) return 8;
  if (value >= 7) return 5;
  return 2;
};

const packageLabel = (rateCard = {}) => {
  if (rateCard.packageName) return rateCard.packageName;
  if (rateCard.maxHours && rateCard.maxKilometers) return `${rateCard.maxHours}HR${rateCard.maxKilometers}KM`;
  if (rateCard.maxDays && rateCard.maxKilometers) return `${rateCard.maxDays}DAY${rateCard.maxKilometers}KM`;
  return rateCard.tripType || 'Package';
};

const getPriceText = (rateCard = {}, trip) => {
  const baseRate = getBaseRateValue(rateCard);
  if (!hasValue(baseRate)) return 'Fare on request';
  if (trip === 'outstation') return `${rupees(baseRate)}/km`;
  return rupees(baseRate);
};

const getPerText = (rateCard = {}, trip) => {
  if (trip === 'local' && rateCard.maxHours && rateCard.maxKilometers) {
    return `/${rateCard.maxHours} hrs (${rateCard.maxKilometers} kms)`;
  }

  if (trip === 'airport' && rateCard.maxKilometers) {
    return `/up to ${rateCard.maxKilometers} kms`;
  }

  return null;
};

const getFeatures = (vehicle = {}, rateCard = {}, trip) => {
  if (trip === 'local') {
    return [
      `${rateCard.maxKilometers || 0} kms included`,
      `${rateCard.maxHours || 0} hours included`,
      `Extra km ${rupees(rateCard.extraKmRate)}`,
    ];
  }

  if (trip === 'outstation') {
    return [
      `${rateCard.maxKilometers || 0} km/day`,
      `Driver bata ${rupees(rateCard.driverBata)}/day`,
      'Toll & parking extra',
    ];
  }

  return [
    `${rateCard.maxKilometers || 0} kms included`,
    `${rateCard.maxHours || 0} hours included`,
    vehicle.fuelType ? `${vehicle.fuelType} vehicle` : 'Airport transfer',
  ].filter(Boolean);
};

const getUiType = (vehicle = {}, trip) => {
  const category = String(vehicle.vehicleCategory || '').replace(/_/g, ' ');
  const type = String(vehicle.vehicleType || '').replace(/_/g, ' ');

  if (trip === 'local') return `${category || type} Package`.trim();
  if (trip === 'outstation') return category || type;
  return category || type;
};

export const toTripId = (tripType = '') => {
  const value = normalizeText(tripType);
  if (value.includes('outstation')) return 'outstation';
  if (value.includes('local')) return 'local';
  return 'airport';
};

export const toBookingTripLabel = (trip = '') => {
  if (trip === 'outstation') return 'Outstation';
  if (trip === 'local') return 'Local Taxi';
  return 'Airport Taxi';
};

export const mapApiVehicleForTrip = (vehicle, trip) => {
  const rateCard = getRateCardForTrip(vehicle, trip);
  if (!vehicle?.status || !rateCard) return null;

  const vehicleBackendId = getVehicleBackendId(vehicle);
  const catalogKey = getVehicleCatalogKey(vehicle);
  const baseRate = getBaseRateValue(rateCard);
  const seats = getSeats(vehicle);
  const bags = getBags(seats);
  const name = getVehicleDisplayName(vehicle);
  const price = getPriceText(rateCard, trip);
  const label = packageLabel(rateCard);

  return {
    id: vehicleBackendId || `${trip}-${catalogKey}`,
    vehicleId: vehicleBackendId,
    backendVehicleId: vehicleBackendId,
    catalogKey,
    apiVehicle: vehicle,
    rateCard,
    selectedRateCard: rateCard,
    registrationNumber: vehicle.registrationNumber,
    vehicleType: vehicle.vehicleType,
    vehicleCategory: vehicle.vehicleCategory,
    fuelType: vehicle.fuelType,
    year: vehicle.year,
    image: getImageKey(vehicle),
    name,
    models: name,
    type: getUiType(vehicle, trip),
    category: vehicle.vehicleCategory || vehicle.vehicleType || 'Vehicle',
    seaters: seats,
    seats,
    bags,
    luggage: bags,
    price,
    per: getPerText(rateCard, trip),
    tag: null,
    badge: null,
    rating: 4.8,
    features: getFeatures(vehicle, rateCard, trip),
    desc: `${name} available for ${toBookingTripLabel(trip).replace(' Taxi', '').toLowerCase()} bookings.`,
    eta: 'Available',
    color: trip === 'airport' ? '#1A73E8' : trip === 'outstation' ? '#FF6B35' : '#0B8A4D',
    availableFor: Array.from(
      new Set(
        (vehicle.rateCards || [])
          .map((item) => {
            if (isAirportRateCard(item)) return 'airport';
            if (isOutstationRateCard(item)) return 'outstation';
            if (isLocalRateCard(item)) return 'local';
            return null;
          })
          .filter(Boolean)
      )
    ),
    packageDetails: {
      label,
      packageName: rateCard.packageName,
      hours: rateCard.maxHours,
      kms: rateCard.maxKilometers,
      days: rateCard.maxDays,
      price,
      baseRate,
      rate: rateCard.rate,
      extraKm: rateCard.extraKmRate,
      extraHr: rateCard.extraHourRate,
      driverBata: rateCard.driverBata,
      location: rateCard.location,
      tripType: rateCard.tripType,
      validFrom: rateCard.validFrom,
      validTo: rateCard.validTo,
    },
    selectedPackage: label,
  };
};

const getVehicleGroupKey = (vehicle = {}) =>
  [
    normalizeText(vehicle.vehicleType || vehicle.name),
    normalizeText(vehicle.vehicleCategory || vehicle.category),
    String(vehicle.capacity || vehicle.seats || vehicle.seaters || ''),
  ].join('|');

const getMappedVehicleScore = (vehicle = {}) => {
  let score = 0;

  if (hasBaseRate(vehicle.selectedRateCard || vehicle.rateCard)) score += 100;
  if (vehicle.price && vehicle.price !== 'Fare on request') score += 50;
  if (vehicle.registrationNumber) score += 10;
  if (getVehicleBackendId(vehicle)) score += 5;

  return score;
};

const dedupeMappedVehicles = (vehicles = []) => {
  const grouped = new Map();

  vehicles.forEach((vehicle) => {
    const key = getVehicleGroupKey(vehicle);
    const existing = grouped.get(key);

    if (!existing || getMappedVehicleScore(vehicle) > getMappedVehicleScore(existing)) {
      grouped.set(key, vehicle);
    }
  });

  return Array.from(grouped.values());
};

const getVehicleDisplayOrderIndex = (vehicle = {}, trip = 'airport') => {
  const order = VEHICLE_DISPLAY_ORDER[trip] || [];
  const text = getVehicleOrderText(vehicle);
  const index = order.findIndex((matchesOrderItem) => matchesOrderItem(text, vehicle));

  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const sortVehiclesForTrip = (vehicles = [], trip = 'airport') =>
  vehicles
    .map((vehicle, index) => ({
      vehicle,
      index,
      orderIndex: getVehicleDisplayOrderIndex(vehicle, trip),
    }))
    .sort((first, second) => {
      if (first.orderIndex !== second.orderIndex) return first.orderIndex - second.orderIndex;
      return first.index - second.index;
    })
    .map(({ vehicle }) => vehicle);

export const groupVehiclesByTrip = (vehicles = []) => ({
  airport: sortVehiclesForTrip(
    dedupeMappedVehicles(
      mergeCatalogVehicles(vehicles).map((vehicle) => mapApiVehicleForTrip(vehicle, 'airport')).filter(Boolean)
    ),
    'airport'
  ),
  outstation: sortVehiclesForTrip(
    dedupeMappedVehicles(
      mergeCatalogVehicles(vehicles).map((vehicle) => mapApiVehicleForTrip(vehicle, 'outstation')).filter(Boolean)
    ),
    'outstation'
  ),
  local: sortVehiclesForTrip(
    dedupeMappedVehicles(
      mergeCatalogVehicles(vehicles).map((vehicle) => mapApiVehicleForTrip(vehicle, 'local')).filter(Boolean)
    ),
    'local'
  ),
});

const getVehiclesFromResponseData = (data) => (
  Array.isArray(data) ? data : data?.data || data?.vehicles || data?.value || []
);

const getTotalCount = (response) => Number(response.headers.get('X-Total-Count') || 0);
const getFallbackPublicVehicles = (reason) => {
  console.warn(`${reason} Using static fallback vehicles.`);
  return fallbackPublicVehicles;
};

const getVehicleApiCacheKey = () =>
  Math.floor(Date.now() / VEHICLE_API_CACHE_WINDOW_MS);

export const fetchPublicVehicles = async () => {
  const pageSize = 100;
  const requestPage = async (page = 0) => {
    const url = new URL(`${NOVPRAYAN_PUBLIC_API_BASE_URL}/vehicles`);
    url.searchParams.set('page', String(page));
    url.searchParams.set('size', String(pageSize));
    url.searchParams.set('_cacheWindow', String(getVehicleApiCacheKey()));

    const response = await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-store',
      headers: {
        ...getNovprayanHeaders(),
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.message || data?.error || 'Failed to fetch vehicles');
    }

    return {
      vehicles: getVehiclesFromResponseData(data),
      totalCount: getTotalCount(response),
    };
  };

  try {
    const firstPage = await requestPage(0);
    const vehicles = [...firstPage.vehicles];
    const totalCount = firstPage.totalCount;

    if (!totalCount || vehicles.length >= totalCount) {
      return vehicles.length
        ? vehicles
        : getFallbackPublicVehicles('Public vehicles API returned no vehicles.');
    }

    const totalPages = Math.ceil(totalCount / pageSize);
    const remainingPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => requestPage(index + 1))
    );

    remainingPages.forEach((page) => {
      vehicles.push(...page.vehicles);
    });

    return vehicles.length
      ? vehicles
      : getFallbackPublicVehicles('Public vehicles API returned no vehicles.');
  } catch (error) {
    console.warn('Unable to fetch public vehicles:', error);
    return getFallbackPublicVehicles('Unable to fetch public vehicles.');
  }
};

export const buildBookingDataFromVehicle = (vehicle, trip = 'airport') => {
  const rateCard = vehicle.selectedRateCard || vehicle.rateCard || vehicle.packageDetails || {};
  const baseRate = getBaseRateValue(rateCard) ?? vehicle.packageDetails?.baseRate;
  const label = toBookingTripLabel(trip);

  return {
    vehicle,
    route: {
      routeName: `${vehicle.name} ${label}`,
      pickup: '',
      dropoff: '',
      distance: rateCard.maxKilometers || vehicle.packageDetails?.kms || '',
      estimatedTime: rateCard.maxHours ? `${rateCard.maxHours} hours` : '',
      price: vehicle.price,
      tripDirection: label,
      tripType: trip,
    },
    pricing: {
      basePrice: baseRate ?? vehicle.price,
      includedKms: rateCard.maxKilometers ?? vehicle.packageDetails?.kms,
      includedHours: rateCard.maxHours ?? vehicle.packageDetails?.hours,
      includedDays: rateCard.maxDays ?? vehicle.packageDetails?.days,
      extraPerKm: rateCard.extraKmRate ?? vehicle.packageDetails?.extraKm,
      extraPerHour: rateCard.extraHourRate ?? vehicle.packageDetails?.extraHr,
      driverBata: rateCard.driverBata ?? vehicle.packageDetails?.driverBata,
      selectedPackage: vehicle.selectedPackage,
      packageDetails: vehicle.packageDetails,
      tolls: 'Extra',
    },
  };
};
